import React, { useEffect, useState, useRef } from 'react';
import { collection, onSnapshot, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import _ from 'lodash';
import Button from './Button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const translations = {
  company: {
    th: 'บริษัท',
    en: 'Company'
  },
  items: {
    th: 'รายการ',
    en: 'Items'
  },
  startDate: {
    th: 'วันที่เริ่ม',
    en: 'Start Date'
  },
  endDate: {
    th: 'วันที่สิ้นสุด',
    en: 'End Date'
  },
  downloadPdf: {
    th: 'ดาวน์โหลด PDF',
    en: 'Download PDF'
  },
  selectDates: {
    th: 'เลือกวันที่',
    en: 'Select Dates'
  },
  pricePerUnit: {
    th: 'ราคาต่อหน่วย',
    en: 'Price Per Unit'
  },
  addItem: {
    th: 'เพิ่มรายการ',
    en: 'Add Item'
  },
  invoice: {
    th: 'ใบแจ้งหนี้',
    en: 'Invoice'
  },
  date: {
    th: 'วันที่',
    en: 'Date'
  },
  branch: {
    th: 'สาขา',
    en: 'Branch'
  },
  taxId: {
    th: 'เลขประจำตัวผู้เสียภาษี',
    en: 'Tax ID'
  },
  order: {
    th: 'ลำดับ',
    en: 'Order'
  },
  unit: {
    th: 'หน่วย',
    en: 'Unit'
  },
  total: {
    th: 'รวม',
    en: 'Total'
  },
  day: {
    th: 'วัน',
    en: 'days'
  },
  tax: {
    th: 'ภาษี',
    en: 'Tax'
  },
  finalAmount: {
    th: 'ยอดสุทธิ',
    en: 'Final Amount'
  },
  phone: {
    th: 'โทรศัพท์',
    en: 'Phone'
  },
  contact: {
    th: 'ผู้ติดต่อ',
    en: 'Contact'
  },
  department: {
    th: 'แผนก',
    en: 'Department'
  },
  saveInvoice: {
    th: 'บันทึกใบแจ้งหนี้',
    en: 'Save Invoice'
  }
};

const formatDate = (date, lang) => {
  if (!date) return '';
  return date.toLocaleDateString(lang === 'th' ? 'th-TH' : 'en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const calculateDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const diffTime = Math.abs(new Date(endDate) - new Date(startDate));
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const AdminInvoices = ({ lang }) => {
  const [invoices, setInvoices] = useState([]);
  const [showGenerator, setShowGenerator] = useState(false);
  const [language, setLanguage] = useState(lang);
  const printRef = useRef(null);

  const [newItem, setNewItem] = useState({
    description: { th: '', en: '' },
    startDate: null,
    endDate: null,
    pricePerUnit: 0
  });

  const [invoiceData, setInvoiceData] = useState({
    date: formatDate(new Date(), lang),
    companyName: {
      th: 'บริษัท เอเชีย คอมโพสิต แมททีเรียล (ไทยแลนด์) จำกัด',
      en: 'Asia Composite Material (Thailand) Co., Ltd.'
    },
    branchNo: '00001',
    taxId: '010555401215',
    items: [],
    phone: '021-3-89994-5',
    contact: {
      th: 'ชุมพล ชุมพึง',
      en: 'Chumpol Chumpueng'
    },
    department: {
      th: 'หนังงานลงทะเปลากแดง',
      en: 'Registration Department'
    }
  });

  const [pdfPreview, setPdfPreview] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "invoices"), (querySnapshot) => {
      const invoicesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInvoices(_.orderBy(invoicesData, ['date'], ['desc']));
    }, (error) => {
      console.error("Error fetching invoices:", error);
    });

    return () => unsubscribe();
  }, []);

  const addItem = () => {
    const days = calculateDays(newItem.startDate, newItem.endDate);
    if (days <= 0 || !newItem.description[language] || !newItem.pricePerUnit) return;

    const item = {
      ...newItem,
      id: invoiceData.items.length + 1,
      quantity: days,
      unit: { th: 'วัน', en: 'day' },
      dateRange: `${formatDate(newItem.startDate, lang)} - ${formatDate(newItem.endDate, lang)}`
    };

    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItem({
      description: { th: '', en: '' },
      startDate: null,
      endDate: null,
      pricePerUnit: 0
    });
  };

  const calculateTotal = () => {
    const subtotal = invoiceData.items.reduce((sum, item) => 
      sum + (item.quantity * item.pricePerUnit), 0
    );
    const taxRate = invoiceData.tax || 0.03;
    const tax = subtotal * taxRate;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`invoice-${formatDate(new Date())}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const generatePdfPreview = async () => {
    if (!printRef.current) return;
    setLoadingPdf(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true
      });
      const imgData = canvas.toDataURL('image/png');
      setPdfPreview(imgData);
    } catch (error) {
      console.error("Error generating PDF preview:", error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleSaveNewInvoice = async () => {
    try {
      const newInvoiceData = {
        ...invoiceData,
        createdAt: new Date().toISOString()
      };
      await addDoc(collection(db, 'invoices'), newInvoiceData);
      setShowGenerator(false);
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  const { subtotal, tax, total } = calculateTotal();

  return (
    <div className="p-6 bg-gray-50">
      <h1 className="text-3xl font-bold mb-4">{lang === 'th' ? 'ใบแจ้งหนี้ของผู้ดูแลระบบ' : 'Admin Invoices'}</h1>
      <Button onClick={() => setShowGenerator(true)} className="bg-yellow-500 text-white">
        Generate New Invoice
      </Button>
      {showGenerator && (
        <div className="my-4">
          <Card className="mb-4">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={language === 'en'}
                    onCheckedChange={(checked) => setLanguage(checked ? 'en' : 'th')}
                  />
                  <Label>{language === 'th' ? 'Thai' : 'English'}</Label>
                </div>
                <Button onClick={handleDownloadPDF}>
                  {translations.downloadPdf[language]}
                </Button>
              </div>

              <div className="space-y-4 mb-8">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder={translations.company[language]}
                    value={invoiceData.companyName[language]}
                    onChange={(e) => setInvoiceData(prev => ({
                      ...prev,
                      companyName: {
                        ...prev.companyName,
                        [language]: e.target.value
                      }
                    }))}
                  />
                  <Input
                    placeholder={translations.items[language]}
                    value={newItem.description[language]}
                    onChange={(e) => setNewItem(prev => ({
                      ...prev,
                      description: {
                        ...prev.description,
                        [language]: e.target.value
                      }
                    }))}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newItem.startDate ? formatDate(newItem.startDate) : translations.startDate[language]}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={newItem.startDate}
                        onSelect={(date) => setNewItem(prev => ({ ...prev, startDate: date }))}
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newItem.endDate ? formatDate(newItem.endDate) : translations.endDate[language]}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={newItem.endDate}
                        onSelect={(date) => setNewItem(prev => ({ ...prev, endDate: date }))}
                      />
                    </PopoverContent>
                  </Popover>

                  <Input
                    type="number"
                    placeholder={translations.pricePerUnit[language]}
                    value={newItem.pricePerUnit}
                    onChange={(e) => setNewItem(prev => ({
                      ...prev,
                      pricePerUnit: Number(e.target.value)
                    }))}
                  />
                </div>
                <Button onClick={addItem}>{translations.addItem[language]}</Button>
              </div>

              <div ref={printRef} className="border p-4 bg-white">
                <div className="text-center mb-4">
                  <div className="text-lg">
                    {translations.invoice[language]} {translations.date[language]} {invoiceData.date}
                  </div>
                  <div>{invoiceData.companyName[language]} ({translations.branch[language]} {invoiceData.branchNo})</div>
                  <div>{translations.taxId[language]} {invoiceData.taxId}</div>
                </div>

                <table className="w-full mb-4">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">{translations.order[language]}</th>
                      <th className="text-left p-2">{translations.items[language]}</th>
                      <th className="text-center p-2">{translations.unit[language]}</th>
                      <th className="text-right p-2">{translations.pricePerUnit[language]}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, index) => (
                      <tr key={item.id} className="border-b">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">
                          {item.description[language]}
                          <div className="text-sm text-gray-500">{item.dateRange}</div>
                        </td>
                        <td className="text-center p-2">{item.quantity}{item.unit[language]}</td>
                        <td className="text-right p-2">{item.pricePerUnit.toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="border-b font-bold">
                      <td colSpan={2} className="p-2">{translations.total[language]}</td>
                      <td className="text-center p-2">
                        {invoiceData.items.reduce((sum, item) => sum + item.quantity, 0)}
                        {translations.day[language]}
                      </td>
                      <td className="text-right p-2">{subtotal.toLocaleString()}</td>
                    </tr>
                    <tr className="border-b">
                      <td colSpan={3} className="p-2">{translations.tax[language]}</td>
                      <td className="text-right p-2">{tax.toLocaleString()}</td>
                    </tr>
                    <tr className="font-bold">
                      <td colSpan={3} className="p-2">{translations.finalAmount[language]}</td>
                      <td className="text-right p-2">{total.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-8">
                  <div>{translations.phone[language]}: {invoiceData.phone}</div>
                  <div>{translations.contact[language]}: {invoiceData.contact[language]}</div>
                  <div>{translations.department[language]}: {invoiceData.department[language]}</div>
                </div>
              </div>
              <div className="flex gap-4 mb-4">
                <Button onClick={handleDownloadPDF}>
                  {translations.downloadPdf[lang]}
                </Button>
                <Button onClick={generatePdfPreview} disabled={loadingPdf}>
                  {loadingPdf ? "Loading Preview..." : "Preview PDF"}
                </Button>
              </div>
              <Button onClick={handleSaveNewInvoice}>
                {translations.saveInvoice[language]}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      {pdfPreview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setPdfPreview(null)}
        >
          <div 
            className="bg-white rounded p-4 max-w-full max-h-screen overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <img src={pdfPreview} alt="Invoice Preview" className="max-w-full" />
            <div className="mt-4 text-right">
              <Button onClick={() => setPdfPreview(null)}>
                Close Preview
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded-lg">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b">Invoice ID</th>
              <th className="py-2 px-4 border-b">Date</th>
              <th className="py-2 px-4 border-b">Client</th>
              <th className="py-2 px-4 border-b">Amount</th>
              <th className="py-2 px-4 border-b">Status</th>
              <th className="py-2 px-4 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(invoice => (
              <tr key={invoice.id}>
                <td className="py-2 px-4 border-b">#{invoice.id.substring(0, 6).toUpperCase()}</td>
                <td className="py-2 px-4 border-b">{new Date(invoice.date).toLocaleDateString()}</td>
                <td className="py-2 px-4 border-b">{invoice.client}</td>
                <td className="py-2 px-4 border-b">${invoice.amount ? invoice.amount.toLocaleString() : '0'}</td>
                <td className="py-2 px-4 border-b">{invoice.status}</td>
                <td className="py-2 px-4 border-b">
                  {/* Removed Delete button */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminInvoices;